import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled, { useTheme, css } from 'styled-components';
import { cardBackground, cardBorder, cardShadow } from '../styles/themeHelpers';
import { getPublicFieldMapById, getPublicFieldById, getPublicOperationTemplates, FieldMap, PublicOperationTemplate } from '../services/fieldService';
import { Field } from '../types/field';
import { MissionMapData, MissionDevicePosition } from '../types/gameDetail';
import GamesSection from '../components/field/GamesSection';
import ModernBackground from '../components/common/ModernBackground';
import ReviewSummary from '../components/common/ReviewSummary';
import ReviewModal from '../components/common/ReviewModal';
import MissionMapViewer from '../components/mission/MissionMapViewer';
import reviewService from '../services/reviewService';
import { ReviewSummary as ReviewSummaryType, Review } from '../types/review';
import { useAuth } from '../contexts/AuthContext';
import { useFieldAuth } from '../contexts/FieldContext';
import { useRefereeAuth } from '../contexts/RefereeContext';

// Theme helpers
const shadowCardBackground = (theme: any) => cardBackground(theme);
const shadowCardBorder = (theme: any) => cardBorder(theme);
const shadowCardShadow = (theme: any) => cardShadow(theme);
const shadowHeaderBackground = (theme: any) => theme.styles?.headerBackground ?? shadowCardBackground(theme);
const shadowSpinnerBorder = (theme: any) => theme.colors?.borderLight ?? 'rgba(0, 255, 170, 0.3)';
const shadowSpinnerTopBorder = (theme: any) => theme.colors?.primary ?? '#00d4ff';

const shadowGridChrome = css`
  ${({ theme }) => theme.name === 'shadowGrid' && css`
    position: relative;
    border: 1px solid rgba(0, 212, 255, 0.35);
    background: ${cardBackground(theme)};
    box-shadow: 0 20px 45px rgba(0, 0, 0, 0.6), 0 0 28px rgba(0, 212, 255, 0.2);
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      border: 1px solid rgba(0, 212, 255, 0.2);
      opacity: 0.25;
      pointer-events: none;
    }
  `}
`;

interface ThemeProps {
  $night?: boolean;
}

const Background = styled.div<ThemeProps>`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients.background || theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const Container = styled.div<ThemeProps>`
  max-width: 1200px;
  margin: 0 auto;
  padding: 100px 20px 40px 20px;
  position: relative;
  z-index: 2;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const BackButton = styled.button<ThemeProps>`
  background: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? 'rgba(0, 212, 255, 0.1)' : 'transparent')};
  border: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.primary : 'rgba(0, 212, 255, 0.3)')};
  color: ${({ theme }) => theme.colors.primary};
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  margin-bottom: 24px;
  transition: all 0.3s ease;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  text-transform: ${({ $night }) => ($night ? 'uppercase' : 'none')};
  letter-spacing: ${({ $night }) => ($night ? '1px' : 'normal')};

  &:hover {
    background: rgba(0, 212, 255, 0.15);
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const Header = styled.div<ThemeProps>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
  padding: 24px;
  background: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return theme.colors.surface;
    return $night ? theme.colors.surface : shadowHeaderBackground(theme);
  }};
  border: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return `1px solid ${theme.colors.borderLight}`;
    return $night ? 'none' : `1px solid ${shadowCardBorder(theme)}`;
  }};
  border-radius: ${({ $night }) => ($night ? '0' : '16px')};
  ${({ $night, theme }) => (!$night && theme.name === 'shadowGrid') && shadowGridChrome};
`;

const MapName = styled.h1<ThemeProps>`
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.primary : '#00d4ff')};
  margin: 0;
  font-size: 2rem;
  font-weight: bold;
  text-shadow: 0 0 20px ${({ $night }) => ($night ? 'rgba(0, 212, 255, 0.35)' : 'rgba(0, 212, 255, 0.5)')};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Rajdhani', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  text-transform: ${({ $night }) => ($night ? 'uppercase' : 'none')};
  letter-spacing: ${({ $night }) => ($night ? '2px' : 'normal')};
`;

const MapDescription = styled.p<ThemeProps>`
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.textSecondary : '#b0b0b0')};
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const SectionCard = styled.div<ThemeProps & { $allowOverflow?: boolean }>`
  background: ${({ $night, theme }) => {
    if ($night || theme.name === 'modernWarfare') return theme.colors.surface;
    return 'transparent';
  }};
  border: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderLight : shadowCardBorder(theme))};
  border-radius: ${({ $night }) => ($night ? '16px' : '16px')};
  overflow: ${({ $allowOverflow }) => ($allowOverflow ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  box-shadow: ${({ $night, theme }) => ($night ? theme.shadows.card : shadowCardShadow(theme))};
  margin-bottom: 24px;
  ${({ theme }) => theme.name === 'shadowGrid' && shadowGridChrome};
`;

const SectionHeader = styled.div<ThemeProps>`
  background: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return theme.colors.surfaceHover;
    return $night ? '#132235' : shadowHeaderBackground(theme);
  }};
  padding: 16px 20px;
  border-bottom: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderMuted : 'rgba(0, 255, 170, 0.12)')};
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const SectionTitle = styled.h3<ThemeProps>`
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.primary : '#00d4ff')};
  margin: 0;
  font-size: 1.1rem;
  font-weight: bold;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Rajdhani', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
  letter-spacing: ${({ $night }) => ($night ? '1px' : 'normal')};
  text-transform: ${({ $night }) => ($night ? 'uppercase' : 'none')};
`;

const SectionContent = styled.div`
  padding: 20px;
`;

const TemplateSelector = styled.div<ThemeProps>`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.surfaceHover : 'rgba(0, 20, 40, 0.4)')};
  border: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderLight : 'rgba(0, 212, 255, 0.2)')};
  border-radius: 8px;

  label {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 0.9rem;
    white-space: nowrap;
  }

  select {
    flex: 1;
    max-width: 300px;
    padding: 8px 12px;
    background: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.surface : 'rgba(0, 20, 40, 0.6)')};
    border: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderLight : 'rgba(0, 212, 255, 0.3)')};
    border-radius: 6px;
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.9rem;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
    }

    option {
      background: ${({ theme }) => theme.colors.surface};
      color: ${({ theme }) => theme.colors.text};
    }
  }
`;

const SchemaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 180px));
  gap: 12px;
`;

const MediaCard = styled.div<ThemeProps>`
  background: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.surfaceHover : 'rgba(0, 20, 40, 0.6)')};
  border: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderLight : 'rgba(0, 212, 255, 0.2)')};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }
`;

const SchemaImage = styled.img`
  width: 100%;
  height: 100px;
  object-fit: cover;
  display: block;
`;

const VideoContainer = styled.div<ThemeProps>`
  background: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.surfaceHover : 'rgba(0, 20, 40, 0.6)')};
  border: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderLight : 'rgba(0, 212, 255, 0.2)')};
  border-radius: 6px;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 16 / 9;
`;

const YouTubeEmbed = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const PhotosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 180px));
  gap: 12px;
`;

const PhotoCard = styled.div<ThemeProps>`
  background: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.surfaceHover : 'rgba(0, 20, 40, 0.6)')};
  border: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderLight : 'rgba(0, 212, 255, 0.2)')};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }
`;

const Photo = styled.img`
  width: 100%;
  height: 100px;
  object-fit: cover;
`;

const VideosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 240px));
  gap: 12px;
`;

const LoadingSpinner = styled.div<ThemeProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;

  &::after {
    content: '';
    width: 50px;
    height: 50px;
    border: 5px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderLight : shadowSpinnerBorder(theme))};
    border-top: 5px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.primary : shadowSpinnerTopBorder(theme))};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div<ThemeProps>`
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.3);
  color: #ff6b6b;
  padding: 24px;
  border-radius: 12px;
  text-align: center;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const EmptyState = styled.div<ThemeProps>`
  text-align: center;
  color: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.textSecondary : '#666')};
  font-style: italic;
  padding: 40px 20px;
  font-family: ${({ $night, theme }) => {
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return $night ? "'Courier New', monospace" : 'inherit';
  }};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: #fff;
  }
`;

const StatisticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
`;

const StatCard = styled.div<ThemeProps>`
  background: ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.surfaceHover : 'rgba(0, 20, 40, 0.6)')};
  border: 1px solid ${({ $night, theme }) => ($night || theme.name === 'modernWarfare' ? theme.colors.borderLight : 'rgba(0, 212, 255, 0.2)')};
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const StatValue = styled.div<ThemeProps>`
  font-size: 2rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 8px;
  text-shadow: 0 0 10px ${({ theme }) => theme.colors.primary}40;
`;

const StatLabel = styled.div<ThemeProps>`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const SectionHeaderWithInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  overflow: visible;
`;

const InfoIcon = styled.div<ThemeProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary}20;
  border: 1px solid ${({ theme }) => theme.colors.primary}40;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  font-weight: bold;
  cursor: help;

  &:hover > div {
    opacity: 1;
    visibility: visible;
  }
`;

const TooltipText = styled.div<ThemeProps>`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  padding: 12px;
  width: 280px;
  font-size: 0.85rem;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s, visibility 0.2s;
  z-index: 99999;
  text-transform: none;
  letter-spacing: normal;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 20px;
    border: 6px solid transparent;
    border-bottom-color: ${({ theme }) => theme.colors.borderLight};
  }
`;

const getYouTubeVideoId = (url: string): string | null => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

const FieldMapDetailPage: React.FC = () => {
  const { fieldId, mapId } = useParams<{ fieldId: string; mapId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isNight = theme.name === 'nightOps';
  const { user } = useAuth();
  const { field: authField } = useFieldAuth();
  const { referee } = useRefereeAuth();

  // Determine back URL based on 'from' parameter
  const fromPage = searchParams.get('from');
  let backUrl = `/fields/${fieldId}?tab=maps`; // Default
  if (fromPage === 'main-referee') {
    backUrl = '/referee/manage-field?tab=maps';
  } else if (fromPage === 'field') {
    // Owner coming from Management tab
    backUrl = `/fields/${fieldId}?tab=management`;
  } else if (fromPage === 'maps') {
    // Coming from public Maps tab
    backUrl = `/fields/${fieldId}?tab=maps`;
  }

  const [map, setMap] = useState<FieldMap | null>(null);
  const [field, setField] = useState<Field | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Operation templates state
  const [operationTemplates, setOperationTemplates] = useState<PublicOperationTemplate[]>([]);
  const [selectedOperationName, setSelectedOperationName] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  // Review states
  const [reviewSummary, setReviewSummary] = useState<ReviewSummaryType | null>(null);
  const [myReview, setMyReview] = useState<Review | null>(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!mapId || !fieldId) return;

      setLoading(true);
      setError(null);
      try {
        const [mapData, fieldData, templates] = await Promise.all([
          getPublicFieldMapById(mapId),
          getPublicFieldById(fieldId),
          getPublicOperationTemplates(mapId)
        ]);
        setMap(mapData);
        setField(fieldData);
        setOperationTemplates(templates);
      } catch (err) {
        console.error('Failed to load data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [mapId, fieldId]);

  // Load reviews
  useEffect(() => {
    const loadReviews = async () => {
      if (!mapId) return;

      setReviewsLoading(true);
      try {
        const [summaryData, myReviewData] = await Promise.all([
          reviewService.getMapReviewSummary(mapId),
          user ? reviewService.getMyReview('map', mapId) : Promise.resolve(null)
        ]);
        setReviewSummary(summaryData);
        setMyReview(myReviewData);
      } catch (err) {
        console.error('Failed to load reviews:', err);
      } finally {
        setReviewsLoading(false);
      }
    };

    loadReviews();
  }, [mapId, user]);

  const handleReviewSubmitted = async () => {
    setIsReviewModalOpen(false);
    // Reload reviews
    if (mapId) {
      try {
        const [summaryData, myReviewData] = await Promise.all([
          reviewService.getMapReviewSummary(mapId),
          user ? reviewService.getMyReview('map', mapId) : Promise.resolve(null)
        ]);
        setReviewSummary(summaryData);
        setMyReview(myReviewData);
      } catch (err) {
        console.error('Failed to reload reviews:', err);
      }
    }
  };

  const showModernBackground = theme.name === 'modernWarfare';

  // Get unique operation names from templates
  const uniqueOperationNames = useMemo(() => {
    const names = operationTemplates
      .map(t => t.operationName)
      .filter(name => name && name.trim() !== '');
    return Array.from(new Set(names)).sort();
  }, [operationTemplates]);

  // Get templates filtered by selected operation
  const filteredTemplates = useMemo(() => {
    if (!selectedOperationName) return [];
    return operationTemplates.filter(t => t.operationName === selectedOperationName);
  }, [operationTemplates, selectedOperationName]);

  // Get selected template
  const selectedTemplate = useMemo(() => {
    return operationTemplates.find(t => t.id === selectedTemplateId) || null;
  }, [operationTemplates, selectedTemplateId]);

  // Reset template selection when operation changes
  useEffect(() => {
    setSelectedTemplateId(null);
  }, [selectedOperationName]);

  // Convert FieldMap to MissionMapData format
  const mapDataForViewer: MissionMapData | null = useMemo(() => {
    if (!map) return null;
    return {
      id: map.id,
      name: map.name,
      schemaImageUrl: map.schemaImageUrl,
      exportedImageUrl: map.exportedImageUrl || undefined,
      schemaElements: map.schemaElements?.map(el => ({
        id: el.id,
        type: el.type,
        x: el.x,
        y: el.y,
        width: el.width,
        height: el.height,
        radius: el.radius,
        points: el.points,
        fill: el.fill,
        stroke: el.stroke,
        strokeWidth: el.strokeWidth,
        text: el.text,
        fontSize: el.fontSize,
        label: el.label,
        iconType: el.iconType,
        rotation: el.rotation,
      })),
      canvasBgColor: (map as any).canvasBgColor || 'dark'
    };
  }, [map]);

  // Convert template devices to MissionDevicePosition format
  const devicesForViewer: MissionDevicePosition[] = useMemo(() => {
    if (!selectedTemplate) return [];
    return selectedTemplate.devices.map(d => ({
      entryId: d.entryId,
      deviceCatalogId: d.deviceCatalogId,
      deviceName: d.deviceName,
      deviceType: d.deviceType,
      iconUrl: d.iconUrl,
      positionX: d.positionX,
      positionY: d.positionY,
      scale: d.scale
    }));
  }, [selectedTemplate]);

  if (loading) {
    return (
      <Background $night={isNight}>
        {showModernBackground && <ModernBackground />}
        <Container $night={isNight}>
          <LoadingSpinner $night={isNight} />
        </Container>
      </Background>
    );
  }

  if (error || !map) {
    return (
      <Background $night={isNight}>
        {showModernBackground && <ModernBackground />}
        <Container $night={isNight}>
          <BackButton $night={isNight} onClick={() => navigate(-1)}>
            ← Back
          </BackButton>
          <ErrorMessage $night={isNight}>{error || 'Map not found'}</ErrorMessage>
        </Container>
      </Background>
    );
  }

  const schemaVideoId = map.schemaVideoUrl ? getYouTubeVideoId(map.schemaVideoUrl) : null;
  const hasPhotos = map.images && map.images.length > 0;
  const hasVideos = map.youtubeVideos && map.youtubeVideos.length > 0;

  return (
    <Background $night={isNight}>
      {showModernBackground && <ModernBackground />}
      <Container $night={isNight}>
        <BackButton $night={isNight} onClick={() => navigate(backUrl)}>
          ← Back to Field
        </BackButton>

        <Header $night={isNight}>
          <MapName $night={isNight}>{field?.name} - {map.name}</MapName>
          {map.description && <MapDescription $night={isNight}>{map.description}</MapDescription>}
        </Header>

        {/* Interactive Map with Template Selector */}
        {mapDataForViewer && (
          <SectionCard $night={isNight}>
            <SectionHeader $night={isNight}>
              <SectionTitle $night={isNight}>Map Schema</SectionTitle>
            </SectionHeader>
            <SectionContent>
              {/* Operation and Template selectors - only show if there are templates */}
              {operationTemplates.length > 0 && uniqueOperationNames.length > 0 && (
                <>
                  {/* Operation selector */}
                  <TemplateSelector $night={isNight}>
                    <label>Operation:</label>
                    <select
                      value={selectedOperationName || ''}
                      onChange={(e) => setSelectedOperationName(e.target.value || null)}
                    >
                      <option value="">Select operation</option>
                      {uniqueOperationNames.map(name => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </TemplateSelector>

                  {/* Template selector - only show if operation is selected */}
                  {selectedOperationName && filteredTemplates.length > 0 && (
                    <TemplateSelector $night={isNight}>
                      <label>Template:</label>
                      <select
                        value={selectedTemplateId || ''}
                        onChange={(e) => setSelectedTemplateId(e.target.value || null)}
                      >
                        <option value="">Select template</option>
                        {filteredTemplates.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </TemplateSelector>
                  )}
                </>
              )}

              {/* Interactive Map Viewer */}
              <MissionMapViewer
                mapData={mapDataForViewer}
                devices={devicesForViewer}
                showDevices={devicesForViewer.length > 0}
                showLabels={true}
                height="500px"
                fieldId={fieldId}
                fieldName={field?.name}
                operationTemplateName={selectedTemplate?.name}
              />

              {/* Schema Video if exists */}
              {schemaVideoId && (
                <div style={{ marginTop: '20px' }}>
                  <VideoContainer $night={isNight}>
                    <YouTubeEmbed
                      src={`https://www.youtube.com/embed/${schemaVideoId}`}
                      title="Schema Video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </VideoContainer>
                </div>
              )}
            </SectionContent>
          </SectionCard>
        )}

        {hasPhotos && (
          <SectionCard $night={isNight}>
            <SectionHeader $night={isNight}>
              <SectionTitle $night={isNight}>Photos ({map.images.length})</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <PhotosGrid>
                {map.images.map((imageUrl, index) => (
                  <PhotoCard $night={isNight} key={index} onClick={() => setSelectedImage(imageUrl)}>
                    <Photo src={imageUrl} alt={`Photo ${index + 1}`} />
                  </PhotoCard>
                ))}
              </PhotosGrid>
            </SectionContent>
          </SectionCard>
        )}

        {hasVideos && (
          <SectionCard $night={isNight}>
            <SectionHeader $night={isNight}>
              <SectionTitle $night={isNight}>Videos ({map.youtubeVideos.length})</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <VideosGrid>
                {map.youtubeVideos.map((videoUrl, index) => {
                  const videoId = getYouTubeVideoId(videoUrl);
                  if (!videoId) return null;
                  return (
                    <VideoContainer $night={isNight} key={index}>
                      <YouTubeEmbed
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title={`Video ${index + 1}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </VideoContainer>
                  );
                })}
              </VideosGrid>
            </SectionContent>
          </SectionCard>
        )}

        {/* Map Reviews */}
        <SectionCard $night={isNight}>
          <SectionHeader $night={isNight}>
            <SectionTitle $night={isNight}>Map Reviews</SectionTitle>
          </SectionHeader>
          <SectionContent>
            <ReviewSummary
              summary={reviewSummary || {
                rating: 0,
                ratingConfirmed: 0,
                reviewCount: 0,
                reviewCountConfirmed: 0,
                totalReviews: 0,
                ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
                ratingDistributionConfirmed: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
              }}
              loading={reviewsLoading}
              targetType="map"
              targetId={mapId || ''}
              showWriteReview={true}
              canWriteReview={!!user}
              onWriteReview={() => setIsReviewModalOpen(true)}
              writeReviewText={myReview ? 'Edit Review' : 'Write Review'}
              writeReviewMessage={authField ? 'Fields cannot write reviews' : referee ? 'Referees cannot write reviews' : !user ? 'Login to write a review' : undefined}
            />
          </SectionContent>
        </SectionCard>

        {/* Mission Statistics */}
        <SectionCard $night={isNight}>
          <SectionHeader $night={isNight}>
            <SectionTitle $night={isNight}>Mission Statistics</SectionTitle>
          </SectionHeader>
          <SectionContent>
            <StatisticsGrid>
              <StatCard $night={isNight}>
                <StatValue $night={isNight}>{map.missionStatistics?.totalMissions || 0}</StatValue>
                <StatLabel $night={isNight}>Total Missions</StatLabel>
              </StatCard>
              {field?.supportedGameTypes?.includes('airsoft') && (
                <StatCard $night={isNight}>
                  <StatValue $night={isNight}>{map.missionStatistics?.airsoftMissions || 0}</StatValue>
                  <StatLabel $night={isNight}>Airsoft</StatLabel>
                </StatCard>
              )}
              {field?.supportedGameTypes?.includes('paintball') && (
                <StatCard $night={isNight}>
                  <StatValue $night={isNight}>{map.missionStatistics?.paintballMissions || 0}</StatValue>
                  <StatLabel $night={isNight}>Paintball</StatLabel>
                </StatCard>
              )}
              {field?.supportedGameTypes?.includes('speedball') && (
                <StatCard $night={isNight}>
                  <StatValue $night={isNight}>{map.missionStatistics?.speedballMissions || 0}</StatValue>
                  <StatLabel $night={isNight}>Speedball</StatLabel>
                </StatCard>
              )}
            </StatisticsGrid>
          </SectionContent>
        </SectionCard>

        {/* Mission History on this Map */}
        <SectionCard $night={isNight} $allowOverflow>
          <SectionHeader $night={isNight}>
            <SectionHeaderWithInfo>
              <SectionTitle $night={isNight}>Mission History</SectionTitle>
              <InfoIcon $night={isNight}>
                ?
                <TooltipText $night={isNight}>
                  Complete history of all missions played on this map. Browse past games, see team compositions, scores, and detailed match results.
                </TooltipText>
              </InfoIcon>
            </SectionHeaderWithInfo>
          </SectionHeader>
          <SectionContent>
            <GamesSection
              mapId={mapId}
              mapName={map.name}
              supportedGameTypes={['airsoft', 'paintball']}
              showFilters={true}
              showPagination={true}
            />
          </SectionContent>
        </SectionCard>

        {/* Image Modal */}
        {selectedImage && (
          <ModalOverlay onClick={() => setSelectedImage(null)}>
            <CloseButton onClick={() => setSelectedImage(null)}>×</CloseButton>
            <ModalImage src={selectedImage} alt="Full size" onClick={(e) => e.stopPropagation()} />
          </ModalOverlay>
        )}

        {/* Review Modals */}
        <ReviewModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          targetType="map"
          targetId={mapId || ''}
          targetName={map?.name || ''}
          existingReview={myReview}
          onReviewSubmitted={handleReviewSubmitted}
        />
      </Container>
    </Background>
  );
};

export default FieldMapDetailPage;
